import { useState, useCallback, useRef, useEffect } from 'react';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Cards Filters Hook
 *
 * Manages filter state, pagination, sorting, and filter operations for the Cards page.
 * Encapsulates all filter-related logic and state management.
 *
 * @param defaultFilterValues - Default filter values object
 * @param modalType - Optional modal type for conditional behavior
 * @param contractNumber - Contract number for modal mode
 * @param loggedInUser - Current logged in user
 * @param assignedAccountNumber - Assigned account number
 * @returns Filter state and operations
 */

export interface FilterValues {
  pageNumber: number;
  pageSize: number;
  sortDirection: boolean;
  sortColumn: string;
  cfStartRange: string;
  cfEndRange: string;
  cfAccountNumber: string | null;
  cfStatus: string;
  cfIssueDateFrom: any;
  cfIssueDateTo: any;
  cfDriverName: string;
  cfVehicleRegNumber: string;
  cfDomesticNumber: string;
  cfCostCentre: string;
  isFilterClick: boolean;
  triggerFilter: boolean;
}

export interface FilterOperations {
  filterValues: FilterValues;
  filterDisplay: boolean;
  filterApplied: boolean;
  setFilterValues: (values: FilterValues) => void;
  setFilterDisplay: (display: boolean) => void;
  toggleFilter: () => void;
  submitCardFilter: (data: any) => void;
  resetFilterValues: (isFilterClick?: boolean, triggerFilter?: boolean) => void;
  sortCards: (sortColumn: string, isAsc: boolean) => void;
  handlePageValue: (pageNumber: number, pageSize: number) => void;
  isFilterApplied: (data: any) => boolean;
}

export const useCardsFilters = (
  defaultFilterValues: any,
  modalType?: string,
  contractNumber?: string,
  loggedInUser?: string,
  assignedAccountNumber?: string
): FilterOperations => {
  const [filterValues, setFilterValues] = useState<FilterValues>({
    pageNumber: 1,
    pageSize: 10,
    sortDirection: false, // isAsc
    sortColumn: 'IssueDate',
    ...defaultFilterValues,
    isFilterClick: false,
    triggerFilter: false,
  });

  const [filterDisplay, setFilterDisplay] = useState(!modalType);
  const [filterApplied, setFilterApplied] = useState(true);

  // useRef pattern to store unstable callbacks (prevents infinite loops)
  // Store unstable callbacks in refs to prevent re-render loops
  const trackEventRef = useRef(trackCustomEvent);

  // Update ref on every render (doesn't cause re-renders)
  useEffect(() => {
    trackEventRef.current = trackCustomEvent;
  });

  // Check if filter is applied (compare with defaults)
  const isFilterApplied = useCallback(
    (data: any): boolean => {
      const submittedValues = {
        cfStartRange: data?.cfStartRange,
        cfEndRange: data?.cfEndRange,
        cfAccountNumber: data?.cfAccountNumber,
        cfStatus: data?.cfStatus,
        cfIssueDateFrom: data?.cfIssueDateFrom,
        cfIssueDateTo: data?.cfIssueDateTo,
        cfDriverName: data?.cfDriverName,
        cfVehicleRegNumber: data?.cfVehicleRegNumber,
        cfDomesticNumber: data?.cfDomesticNumber,
        cfCostCentre: data?.cfCostCentre,
      };

      const isDefaultValues = Object.keys(defaultFilterValues).every(
        (key) => submittedValues[key] === defaultFilterValues[key]
      );

      return !isDefaultValues;
    },
    [defaultFilterValues]
  );

  // Toggle filter display
  // IMPORTANT: reads filterDisplay (not filterApplied) to match original semantics:
  // "if the panel was open, evaluate whether a filter is actually applied; otherwise default to true"
  const toggleFilter = useCallback(() => {
    setFilterDisplay((prev) => !prev);
    setFilterApplied(filterDisplay ? isFilterApplied(filterValues) : true);
  }, [filterDisplay, filterValues, isFilterApplied]);

  // Submit filter with GTM tracking
  const submitCardFilter = useCallback(
    (data: any) => {
      window.dataLayer.push({
        event: 'Card_Portal_All_Filter',
        button_click: true,
      });

      setFilterApplied(isFilterApplied(data));
      setFilterDisplay(false);

      setFilterValues((prev) => ({
        ...prev,
        ...data,
        pageNumber: 1,
        triggerFilter: true,
        isFilterClick: true,
      }));

      if (trackEventRef.current && loggedInUser && assignedAccountNumber) {
        trackEventRef.current(
          'Submit Filter Cards',
          loggedInUser,
          assignedAccountNumber,
          'Clicked Filter button to filter Cards List.'
        );
      }
    },
    [isFilterApplied, loggedInUser, assignedAccountNumber]
  );

  // Reset filter values to defaults
  const resetFilterValues = useCallback(
    (isFilterClick = false, triggerFilter = false) => {
      setFilterValues((prev) => ({
        ...prev,
        ...defaultFilterValues,
        cfAccountNumber: modalType ? contractNumber : null,
        isFilterClick,
        triggerFilter,
      }));
    },
    [defaultFilterValues, modalType, contractNumber]
  );

  // Sort cards
  const sortCards = useCallback((sortColumn: string, isAsc: boolean) => {
    setFilterValues((prev) => ({
      ...prev,
      sortColumn,
      sortDirection: isAsc,
      triggerFilter: true,
    }));
  }, []);

  // Handle pagination with GTM tracking
  const handlePageValue = useCallback(
    (pageNumber: number, pageSize: number) => {
      window.dataLayer.push({
        event: 'Card_Portal_All_Page_Number',
        page_number: pageNumber,
        page_size: pageSize,
      });

      setFilterValues((prev) => ({
        ...prev,
        pageNumber,
        pageSize,
        triggerFilter: true,
      }));

      if (trackEventRef.current && loggedInUser && assignedAccountNumber) {
        trackEventRef.current(
          'Change Page Number',
          loggedInUser,
          assignedAccountNumber,
          'Clicked to change page of cards list.'
        );
      }
    },
    [loggedInUser, assignedAccountNumber]
  );

  return {
    filterValues,
    filterDisplay,
    filterApplied,
    setFilterValues,
    setFilterDisplay,
    toggleFilter,
    submitCardFilter,
    resetFilterValues,
    sortCards,
    handlePageValue,
    isFilterApplied,
  };
};
